import { useContentDraftGuard } from "../../hooks/useContentDraftGuard";
import React, { useState, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  Textarea,
  IconButton,
  Alert,
  Select,
  Option,
  CircularProgress,
  Card,
  CardContent,
} from "@mui/joy";
import {
  DotsSixVertical as DragIndicator,
  Plus as Add,
  Trash as Delete,
  PencilSimple as Edit,
  FloppyDisk as Save,
  ListBullets as FormatListBulleted,
  TextHOne as Title,
  Code,
  LinkSimple as Link,
} from "@phosphor-icons/react";
import toast from "react-hot-toast";
import { useContentBlocks } from "../../hooks/useContentBlocks";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface ArticleEditorProps {
  articleId: number;
  onClose: () => void;
}

interface ContentBlock {
  id: string;
  block_type: string;
  level?: number;
  content: string;
  order: number;
  metadata?: any;
}

interface SortableBlockProps {
  block: ContentBlock;
  onEdit: (block: ContentBlock) => void;
  onDelete: (id: string) => void;
  isEditing: boolean;
  editContent: string;
  onSave: () => void;
  onCancel: () => void;
  onContentChange: (content: string) => void;
  isSaving: boolean;
}

const SortableBlock: React.FC<SortableBlockProps> = ({
  block,
  onEdit,
  onDelete,
  isEditing,
  editContent,
  onSave,
  onCancel,
  onContentChange,
  isSaving,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: block.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const getBlockIcon = (type: string) => {
    switch (type) {
      case "h":
        return <Title />;
      case "p":
        return <Typography />;
      case "list":
        return <FormatListBulleted />;
      case "cta":
        return <Link />;
      default:
        return <Code />;
    }
  };

  const getBlockLabel = (block: ContentBlock) => {
    if (block.block_type === "h" && block.level) {
      return `Heading ${block.level}`;
    }
    switch (block.block_type) {
      case "p":
        return "Paragraph";
      case "list":
        return "List";
      case "cta":
        return "Call to Action";
      default:
        return block.block_type;
    }
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        position: "relative",
        borderRadius: "sm",
        transition: "background-color 0.2s",
        "&:hover": {
          backgroundColor: "background.level1",
          "& .block-controls": {
            opacity: 1,
          },
        },
        ...(isEditing && {
          border: "2px solid",
          borderColor: "primary.main",
          backgroundColor: "background.surface",
          p: 2,
          borderRadius: "md",
        }),
      }}
    >
      {isEditing ? (
        <Stack spacing={2}>
          {block.block_type === "list" ? (
            <Textarea
              value={editContent}
              onChange={(e) => onContentChange(e.target.value)}
              minRows={3}
              placeholder="Enter list items (one per line)"
            />
          ) : (
            <Textarea
              value={editContent}
              onChange={(e) => onContentChange(e.target.value)}
              minRows={2}
              placeholder="Enter content"
            />
          )}
          <Stack direction="row" spacing={1}>
            <Button
              size="sm"
              onClick={onSave}
              loading={isSaving}
              disabled={isSaving}
              startDecorator={
                isSaving ? <CircularProgress size="sm" /> : <Save />
              }
            >
              {isSaving ? "Saving..." : "Save"}
            </Button>
            <Button
              size="sm"
              variant="plain"
              onClick={onCancel}
              disabled={isSaving}
            >
              Cancel
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Box
          sx={{ display: "flex", alignItems: "start", gap: 1, py: 1.5, px: 2 }}
        >
          {/* Drag Handle - Hidden by default */}
          <Box
            className="block-controls"
            sx={{
              opacity: { xs: 1, md: 0 },
              "&:focus-within": { opacity: 1 },
              transition: "opacity 0.2s",
              display: "flex",
              alignItems: "center",
            }}
          >
            <IconButton
              size="sm"
              variant="plain"
              {...attributes}
              {...listeners}
              sx={{ cursor: "grab", "&:active": { cursor: "grabbing" } }}
            >
              <DragIndicator />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1 }}>
            <Typography
              level={
                block.block_type === "h"
                  ? (`h${block.level || 3}` as any)
                  : "body-md"
              }
              sx={{
                whiteSpace: "pre-wrap",
                mb: block.block_type === "h" ? 2 : 1,
                mt:
                  block.block_type === "h" && block.level === 1
                    ? 3
                    : block.block_type === "h" && block.level === 2
                      ? 2.5
                      : 0,
              }}
            >
              {block.content}
            </Typography>
          </Box>

          {/* Action Buttons - Hidden by default */}
          <Stack
            direction="row"
            spacing={0.5}
            className="block-controls"
            sx={{
              opacity: { xs: 1, md: 0 },
              "&:focus-within": { opacity: 1 },
              transition: "opacity 0.2s",
            }}
          >
            <IconButton
              size="sm"
              variant="plain"
              aria-label="Edit block"
              onClick={() => onEdit(block)}
              sx={{
                "&:hover": {
                  backgroundColor: "background.level2",
                },
              }}
            >
              <Edit />
            </IconButton>
            <IconButton
              size="sm"
              variant="plain"
              color="danger"
              aria-label="Delete block"
              onClick={() => onDelete(block.id)}
              sx={{
                "&:hover": {
                  backgroundColor: "danger.plainHoverBg",
                },
              }}
            >
              <Delete />
            </IconButton>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export const ArticleEditor: React.FC<ArticleEditorProps> = ({
  articleId,
  onClose,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState("");
  const [newBlockType, setNewBlockType] = useState<string>("p");
  const [newBlockLevel, setNewBlockLevel] = useState<number>(2);
  const [showAddBlock, setShowAddBlock] = useState(false);
  const [newBlockContent, setNewBlockContent] = useState("");
  const [savingId, setSavingId] = useState<string | null>(null);
  const [addingBlock, setAddingBlock] = useState(false);
  const [savingRevision, setSavingRevision] = useState(false);
  const dirty = Boolean(editingId || newBlockContent.trim());
  useContentDraftGuard(dirty);

  const {
    blocks,
    isLoading,
    updateBlock,
    deleteBlock,
    addBlock,
    reorderBlocks,
    saveRevision,
  } = useContentBlocks(articleId);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = useCallback(
    (event: any) => {
      if (savingId || editingId || addingBlock || savingRevision) return; // Prevent drag during operations

      const { active, over } = event;

      if (over && active.id !== over.id) {
        const oldIndex = blocks.findIndex(
          (b: ContentBlock) => b.id === active.id,
        );
        const newIndex = blocks.findIndex(
          (b: ContentBlock) => b.id === over.id,
        );
        const newOrder = arrayMove(
          blocks as ContentBlock[],
          oldIndex,
          newIndex,
        );
        reorderBlocks(newOrder);
      }
    },
    [blocks, reorderBlocks, savingId, editingId, addingBlock, savingRevision],
  );

  const handleEdit = useCallback(
    (block: ContentBlock) => {
      if (savingId || editingId) return; // Prevent editing multiple blocks at once
      setEditingId(block.id);
      setEditContent(block.content);
    },
    [savingId, editingId],
  );

  const handleSaveEdit = useCallback(async () => {
    if (!editingId || savingId === editingId) return; // Prevent double saves

    try {
      setSavingId(editingId);
      await updateBlock(editingId, { content: editContent });
      setEditingId(null);
      setEditContent("");
      toast.success("Block saved successfully!");
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Failed to save block:", error);
      }
      toast.error("Failed to save block. Please try again.");
    } finally {
      setSavingId(null);
    }
  }, [editingId, editContent, savingId, updateBlock]);

  const handleCancelEdit = useCallback(() => {
    if (savingId === editingId) return; // Don't allow cancel during save
    setEditingId(null);
    setEditContent("");
  }, [editingId, savingId]);

  const handleAddBlock = useCallback(async () => {
    if (addingBlock) return; // Prevent multiple adds

    const blockData: any = {
      block_type: newBlockType,
      content: newBlockContent,
      order: blocks.length,
    };

    if (newBlockType === "h") {
      blockData.level = newBlockLevel;
    }

    try {
      setAddingBlock(true);
      await addBlock(blockData);
      setShowAddBlock(false);
      setNewBlockContent("");
      toast.success("Block added successfully!");
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Failed to add block:", error);
      }
      toast.error("Failed to add block. Please try again.");
    } finally {
      setAddingBlock(false);
    }
  }, [
    addingBlock,
    newBlockType,
    newBlockContent,
    blocks.length,
    newBlockLevel,
    addBlock,
  ]);

  const handleSaveRevision = useCallback(async () => {
    if (savingRevision) return; // Prevent multiple saves

    try {
      setSavingRevision(true);
      await saveRevision();
      toast.success("Article revision saved successfully!");
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Failed to save revision:", error);
      }
      toast.error("Failed to save revision. Please try again.");
    } finally {
      setSavingRevision(false);
    }
  }, [savingRevision, saveRevision]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3, gap: 1 }}
      >
        <Typography level="body-md" sx={{ color: "text.secondary" }}>
          {blocks.length} content blocks
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            color="primary"
            size="sm"
            startDecorator={<Add />}
            onClick={() => setShowAddBlock(true)}
          >
            Add Block
          </Button>
          <Button
            variant="solid"
            size="sm"
            startDecorator={
              savingRevision ? <CircularProgress size="sm" /> : <Save />
            }
            onClick={handleSaveRevision}
            loading={savingRevision}
            disabled={savingRevision || !!editingId || showAddBlock}
          >
            {savingRevision ? "Saving..." : "Save Revision"}
          </Button>
        </Stack>
      </Stack>

      {/* Add Block Form */}
      {showAddBlock && (
        <Card variant="soft" sx={{ mb: 3 }}>
          <CardContent>
            <Typography level="h4" sx={{ mb: 2 }}>
              Add New Block
            </Typography>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <Select
                  value={newBlockType}
                  onChange={(_, value) => setNewBlockType(value as string)}
                  sx={{ flex: 1 }}
                >
                  <Option value="h">Heading</Option>
                  <Option value="p">Paragraph</Option>
                  <Option value="list">List</Option>
                  <Option value="cta">Call to Action</Option>
                </Select>
                {newBlockType === "h" && (
                  <Select
                    value={newBlockLevel}
                    onChange={(_, value) => setNewBlockLevel(value as number)}
                  >
                    <Option value={1}>H1</Option>
                    <Option value={2}>H2</Option>
                    <Option value={3}>H3</Option>
                  </Select>
                )}
              </Stack>
              <Textarea
                value={newBlockContent}
                onChange={(e) => setNewBlockContent(e.target.value)}
                minRows={3}
                placeholder={
                  newBlockType === "list"
                    ? "Enter list items (one per line)"
                    : "Enter content"
                }
              />
              <Stack direction="row" spacing={1}>
                <Button
                  onClick={handleAddBlock}
                  disabled={!newBlockContent || addingBlock}
                  loading={addingBlock}
                >
                  {addingBlock ? "Adding..." : "Add Block"}
                </Button>
                <Button
                  variant="plain"
                  onClick={() => setShowAddBlock(false)}
                  disabled={addingBlock}
                >
                  Cancel
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      {blocks.length === 0 ? (
        <Alert color="neutral">
          <Typography>
            No content blocks yet. Add blocks to build your article.
          </Typography>
        </Alert>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={blocks.map((b: ContentBlock) => b.id)}
            strategy={verticalListSortingStrategy}
          >
            <Box>
              {blocks.map((block: ContentBlock) => (
                <SortableBlock
                  key={block.id}
                  block={block}
                  onEdit={handleEdit}
                  onDelete={deleteBlock}
                  isEditing={editingId === block.id}
                  editContent={editContent}
                  onSave={handleSaveEdit}
                  onCancel={handleCancelEdit}
                  onContentChange={setEditContent}
                  isSaving={savingId === block.id}
                />
              ))}
            </Box>
          </SortableContext>
        </DndContext>
      )}

      <Stack
        direction="row"
        spacing={2}
        justifyContent="flex-end"
        sx={{ mt: 4 }}
      >
        <Button variant="plain" onClick={onClose}>
          Close
        </Button>
        <Button
          variant="solid"
          onClick={handleSaveRevision}
          loading={savingRevision}
          disabled={savingRevision || !!editingId || showAddBlock}
        >
          {savingRevision ? "Saving version..." : "Save Version"}
        </Button>
      </Stack>
    </Box>
  );
};
